/**
 * Financial Report PDF generator — luxury redesign.
 *
 * Builds an A4-portrait, white-paged report with:
 *   1. Header  — Business name, "Financial Report", period + generated date,
 *                gold accent bar, gold divider rule
 *   2. Balance — per-source revenue breakdown (Source | Total | Parts | Net)
 *   3. Salaries — per-technician breakdown (Name | Revenue | Parts | Salary)
 *   4. Expenses — itemized (Date | Category | Amount | Description)
 *   5. Net Profit Summary — formula breakdown
 *   6. Profit Split — gold "= $X each"
 *   7. Footer — gold rule + "Generated by Fieldz AI · <date>" + page numbers
 *
 * Data sources are passed in from `buildAndDownloadFinancialReportPdf()` in
 * `useFinancialReportPdf.js`. If a section's source data is missing, the PDF
 * gracefully renders a "Data unavailable" notice rather than crashing.
 *
 * Style: modern accountant's report meets luxury brand. White background,
 * dark charcoal section bars, gold (#C9A84C) accents on totals + final figure.
 */
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// ── Palette ─────────────────────────────────────────────────────────
const COLORS = {
  ink: [10, 10, 15],            // #0A0A0F  primary text
  text: [31, 41, 55],           // #1F2937  body text
  muted: [107, 114, 128],       // #6B7280  subtitle / column labels
  light: [156, 163, 175],       // #9CA3AF  meta / dates
  rule: [229, 231, 235],        // #E5E7EB  thin borders
  altRow: [249, 250, 251],      // #F9FAFB  zebra striping
  charcoal: [17, 19, 24],       // #111318  section header bg
  gold: [201, 168, 76],         // #C9A84C  accent
  white: [255, 255, 255],
};

// ── Helpers ─────────────────────────────────────────────────────────
const fmt = (v) =>
  `$${Number(v || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatShortDate = (iso) => {
  // "2026-12-03" → "12/03/2026". Tolerant of bad input.
  if (!iso) return "—";
  try {
    const d = typeof iso === "string" && /^\d{4}-\d{2}-\d{2}/.test(iso)
      ? new Date(iso + "T00:00:00")
      : new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${mm}/${dd}/${d.getFullYear()}`;
  } catch {
    return String(iso);
  }
};

const A4 = { width: 595.28, height: 841.89 };  // pt
const MARGIN = 40;

// ── Section header bar ──────────────────────────────────────────────
const drawSectionHeader = (doc, y, label) => {
  const w = A4.width - MARGIN * 2;
  // dark charcoal bar
  doc.setFillColor(...COLORS.charcoal);
  doc.rect(MARGIN, y, w, 22, "F");
  // gold left-accent bar
  doc.setFillColor(...COLORS.gold);
  doc.rect(MARGIN, y, 3, 22, "F");
  // label
  doc.setTextColor(...COLORS.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(String(label).toUpperCase(), MARGIN + 12, y + 15);
  return y + 22 + 10;  // bottom of bar + spacing
};

const drawDataUnavailable = (doc, y, msg) => {
  doc.setFont("helvetica", "italic");
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.light);
  doc.text(msg, MARGIN + 4, y + 12);
  return y + 26;
};

const drawHeader = (doc, { businessName, periodLabel, generatedAt }) => {
  // Left column
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(...COLORS.ink);
  doc.text(businessName || "Your Business", MARGIN, 60);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(13);
  doc.setTextColor(...COLORS.muted);
  doc.text("Financial Report", MARGIN, 80);

  doc.setFontSize(11);
  doc.setTextColor(...COLORS.light);
  doc.text(`Period: ${periodLabel || "—"}`, MARGIN, 98);
  doc.text(`Generated: ${generatedAt}`, MARGIN, 113);

  // Right side: refined gold accent — thin gold line + restrained "FIELDZ AI"
  // word-mark. Avoids the heavy filled gold block which read as amateurish.
  const wmX = A4.width - MARGIN;
  // Small uppercase wordmark, right-aligned
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.ink);
  // Letter-spaced "FIELDZ AI" — pdf doesn't expose tracking, simulate with spaces
  doc.text("F I E L D Z   A I", wmX, 60, { align: "right" });
  // Thin gold underline
  doc.setDrawColor(...COLORS.gold);
  doc.setLineWidth(1.2);
  doc.line(wmX - 96, 67, wmX, 67);
  doc.setLineWidth(1);
  // Tiny tagline below
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.muted);
  doc.text("FIELD SERVICE INTELLIGENCE", wmX, 80, { align: "right" });

  // Gold divider (thinner — 0.8pt — for a more refined feel)
  doc.setDrawColor(...COLORS.gold);
  doc.setLineWidth(0.8);
  doc.line(MARGIN, 130, A4.width - MARGIN, 130);
  doc.setLineWidth(1);
  return 150;  // y after header
};

const drawFooter = (doc, generatedAt, pageNum, pageTotal) => {
  const footerY = A4.height - 28;
  // Gold thin top rule
  doc.setDrawColor(...COLORS.gold);
  doc.setLineWidth(0.6);
  doc.line(MARGIN, footerY - 12, A4.width - MARGIN, footerY - 12);
  doc.setLineWidth(1);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.light);
  doc.text(`Generated by Fieldz AI · ${generatedAt}`, A4.width / 2, footerY, { align: "center" });
  doc.text(`Page ${pageNum} of ${pageTotal}`, A4.width - MARGIN, footerY, { align: "right" });
};

// ── Tables (uniform style) ──────────────────────────────────────────
const TABLE_BASE = {
  margin: { left: MARGIN, right: MARGIN },
  styles: {
    font: "helvetica",
    fontSize: 10,
    textColor: COLORS.text,
    cellPadding: 5,
    lineColor: COLORS.rule,
    lineWidth: 0.3,
  },
  headStyles: {
    fillColor: COLORS.white,
    textColor: COLORS.muted,
    fontSize: 9,
    fontStyle: "bold",
    cellPadding: 5,
    lineColor: COLORS.rule,
    lineWidth: { bottom: 0.6 },
  },
  // Totals row sits inside the table grid so the amount stays under its
  // numeric column (Net / Salary / Amount). Bold ink label, gold value.
  footStyles: {
    fillColor: COLORS.white,
    textColor: COLORS.ink,
    fontStyle: "bold",
    fontSize: 10,
    cellPadding: 6,
    lineColor: COLORS.rule,
    lineWidth: { top: 1.0 },
  },
  alternateRowStyles: { fillColor: COLORS.altRow },
  theme: "plain",
};

// ── Sections ────────────────────────────────────────────────────────
const drawBalanceSection = (doc, y, sourceRows) => {
  y = drawSectionHeader(doc, y, "Balance");

  if (!sourceRows || sourceRows.length === 0) {
    return drawDataUnavailable(doc, y, "Data unavailable — no completed jobs in selected period.");
  }

  const totalRevenue = sourceRows.reduce((s, r) => s + Number(r.total || 0), 0);
  const totalParts = sourceRows.reduce((s, r) => s + Number(r.parts || 0), 0);
  const totalNet = sourceRows.reduce((s, r) => s + Number(r.net || 0), 0);

  autoTable(doc, {
    startY: y,
    head: [["Source", "Total", "Parts", "Net"]],
    body: sourceRows.map((r) => [r.name, fmt(r.total), fmt(r.parts), fmt(r.net)]),
    foot: [[
      { content: "TOTAL NET BALANCE", styles: { halign: "left" } },
      { content: fmt(totalRevenue), styles: { halign: "right" } },
      { content: fmt(totalParts), styles: { halign: "right" } },
      { content: fmt(totalNet), styles: { halign: "right", textColor: COLORS.gold } },
    ]],
    columnStyles: {
      0: { cellWidth: "auto" },
      1: { halign: "right", cellWidth: 90 },
      2: { halign: "right", cellWidth: 90 },
      3: { halign: "right", cellWidth: 100, fontStyle: "bold" },
    },
    ...TABLE_BASE,
  });
  return doc.lastAutoTable.finalY + 14;
};

const drawSalariesSection = (doc, y, techRows) => {
  y = drawSectionHeader(doc, y, "Salaries");

  if (!techRows || techRows.length === 0) {
    return drawDataUnavailable(doc, y, "Data unavailable — no technician payouts in selected period.");
  }

  const totalBase = techRows.reduce((s, r) => s + Number(r.baseSalary || 0), 0);
  const totalParts = techRows.reduce((s, r) => s + Number(r.parts || 0), 0);
  const totalPayout = techRows.reduce((s, r) => s + Number(r.totalPayout || 0), 0);

  autoTable(doc, {
    startY: y,
    head: [["Technician", "Base Salary", "Parts", "Total Payout"]],
    body: techRows.map((r) => [
      r.name,
      fmt(r.baseSalary),
      fmt(r.parts),
      fmt(r.totalPayout),
    ]),
    foot: [[
      { content: "TOTAL SALARIES", styles: { halign: "left" } },
      { content: fmt(totalBase), styles: { halign: "right", textColor: COLORS.gold } },
      { content: fmt(totalParts), styles: { halign: "right" } },
      { content: fmt(totalPayout), styles: { halign: "right" } },
    ]],
    columnStyles: {
      0: { cellWidth: "auto" },
      1: { halign: "right", cellWidth: 100, fontStyle: "bold" },
      2: { halign: "right", cellWidth: 90 },
      3: { halign: "right", cellWidth: 110 },
    },
    ...TABLE_BASE,
  });
  return doc.lastAutoTable.finalY + 14;
};

const drawExpensesSection = (doc, y, expenses) => {
  y = drawSectionHeader(doc, y, "Expenses");

  if (!expenses || expenses.length === 0) {
    return drawDataUnavailable(doc, y, "No expenses recorded for the selected period.");
  }

  const total = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);

  autoTable(doc, {
    startY: y,
    head: [["Date", "Category", "Amount", "Description"]],
    body: expenses.map((e) => [
      formatShortDate(e.date),
      e.category || "Other",
      fmt(e.amount),
      [e.description, e.notes].filter(Boolean).join(" — "),
    ]),
    foot: [[
      { content: "TOTAL EXPENSES", colSpan: 2, styles: { halign: "left" } },
      { content: fmt(total), styles: { halign: "right", textColor: COLORS.gold } },
      { content: "", styles: {} },
    ]],
    columnStyles: {
      0: { cellWidth: 80 },
      1: { cellWidth: 100 },
      2: { halign: "right", cellWidth: 90, fontStyle: "bold" },
      3: { cellWidth: "auto" },
    },
    ...TABLE_BASE,
  });
  return doc.lastAutoTable.finalY + 14;
};

const drawNetProfit = (doc, y, { balanceAfterParts, baseSalaries, expenses }) => {
  y = drawSectionHeader(doc, y, "Net Profit Summary");

  const netProfit = balanceAfterParts - baseSalaries - expenses;

  // Parts are already removed in the Balance section's Net column — they
  // must NOT appear again here. Only deduct salaries (base only) + expenses.
  const lines = [
    ["Total Net Balance (after parts)", balanceAfterParts, false],
    ["-  Total salaries (base only)", baseSalaries, true],
    ["-  Total expenses", expenses, true],
  ];

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  for (const [label, value, isMinus] of lines) {
    doc.setTextColor(...(isMinus ? COLORS.muted : COLORS.text));
    doc.text(label, MARGIN + 6, y + 12);
    doc.setTextColor(...(isMinus ? [180, 30, 30] : COLORS.text));
    doc.text(fmt(value), A4.width - MARGIN - 6, y + 12, { align: "right" });
    y += 18;
  }

  // divider
  doc.setDrawColor(...COLORS.rule);
  doc.line(MARGIN, y + 2, A4.width - MARGIN, y + 2);
  y += 10;

  // Net profit row — bold + gold
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...COLORS.ink);
  doc.text("NET PROFIT", MARGIN + 6, y + 12);
  doc.setTextColor(...COLORS.gold);
  doc.text(fmt(netProfit), A4.width - MARGIN - 6, y + 12, { align: "right" });
  y += 28;

  return { y, netProfit };
};

const drawProfitSplit = (doc, y, netProfit, profitPartners) => {
  // Empty list = no split. Render a single "Owner" row that takes 100%.
  const rows = (profitPartners && profitPartners.length > 0)
    ? profitPartners
    : [{ name: "Owner", percentage: 100 }];

  y = drawSectionHeader(doc, y, "Profit Split");

  // Use autoTable for consistent alignment with the other sections.
  autoTable(doc, {
    startY: y,
    head: [["Partner", "Share", "Amount"]],
    body: rows.map((p) => {
      const pct = Number(p.percentage) || 0;
      const amount = netProfit * pct / 100;
      return [p.name, `${pct}%`, fmt(amount)];
    }),
    foot: [[
      { content: "TOTAL", styles: { halign: "left" } },
      { content: "100%", styles: { halign: "right" } },
      { content: fmt(netProfit), styles: { halign: "right", textColor: COLORS.gold } },
    ]],
    columnStyles: {
      0: { cellWidth: "auto" },
      1: { halign: "right", cellWidth: 100 },
      2: { halign: "right", cellWidth: 130, fontStyle: "bold", textColor: COLORS.gold },
    },
    ...TABLE_BASE,
  });
  return doc.lastAutoTable.finalY + 14;
};

// ── Page break helper ───────────────────────────────────────────────
const ensureSpace = (doc, y, needed = 120) => {
  // Reserve room for footer (~30) and bottom margin (~20)
  if (y + needed > A4.height - 60) {
    doc.addPage();
    return MARGIN + 10;  // start below top margin on new page
  }
  return y;
};

// ── Main entry point ────────────────────────────────────────────────
/**
 * Build the PDF document.
 *
 * @param {Object} args
 * @param {string} args.businessName
 * @param {string} args.periodLabel
 * @param {Array}  args.sourceRows      – [{name, total, parts, net}]
 * @param {Array}  args.techRows        – [{name, baseSalary, parts, totalPayout}]
 * @param {Array}  args.expenses        – [{date, description, category, amount, notes}]
 * @param {Object} args.totals          – {balanceAfterParts, baseSalaries, expenses}
 * @param {Array}  args.profitPartners  – [{name, percentage}] (sums to 100; empty = single Owner row)
 * @returns {jsPDF}
 */
export const generateFinancialReportPdf = ({
  businessName,
  periodLabel,
  sourceRows = [],
  techRows = [],
  expenses = [],
  totals = { balanceAfterParts: 0, baseSalaries: 0, expenses: 0 },
  taxTotals = null,
  profitPartners = [],
}) => {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const generatedAt = new Date().toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

  // 1. Header
  let y = drawHeader(doc, { businessName, periodLabel, generatedAt });

  // 2. Balance
  y = ensureSpace(doc, y, 100);
  y = drawBalanceSection(doc, y, sourceRows);

  // 3. Salaries
  y = ensureSpace(doc, y, 100);
  y = drawSalariesSection(doc, y, techRows);

  // 4. Expenses
  y = ensureSpace(doc, y, 100);
  y = drawExpensesSection(doc, y, expenses);

  // 5. Net Profit Summary
  y = ensureSpace(doc, y, 140);
  const { y: yAfterNet, netProfit } = drawNetProfit(doc, y, totals);
  y = yAfterNet;

  // 5b. Sales tax info line (excluded from all figures above)
  if (taxTotals && (taxTotals.charged > 0 || taxTotals.collected > 0)) {
    y = ensureSpace(doc, y, 30);
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.muted);
    doc.text(
      `Sales tax — charged: ${fmt(taxTotals.charged)} · collected: ${fmt(taxTotals.collected)}. Tax is excluded from revenue, salaries and profit above.`,
      MARGIN + 6,
      y + 4
    );
    y += 20;
  }

  // 6. Profit Split
  y = ensureSpace(doc, y, 80);
  y = drawProfitSplit(doc, y, netProfit, profitPartners);

  // Footers (after all content so we know the page count)
  const pageTotal = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pageTotal; p++) {
    doc.setPage(p);
    drawFooter(doc, generatedAt, p, pageTotal);
  }

  return doc;
};

export const downloadFinancialReportPdf = (args) => {
  const doc = generateFinancialReportPdf(args);
  const safe = String(args.periodLabel || "period").replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "");
  const filename = `financial_report_${safe}.pdf`;

  // In the Expo WebView wrapper (iOS/Android native app) `doc.save()` triggers
  // a browser download that the WebView silently swallows — no share sheet,
  // no file. Instead, hand the PDF bytes to the native bridge which uses
  // expo-sharing to open the OS share dialog (Save to Files, AirDrop,
  // Mail, Drive, etc).
  const isNative =
    (typeof window !== "undefined" && window.FieldzNative?.isNativeApp) ||
    (typeof window !== "undefined" && window.ReactNativeWebView);

  if (isNative && typeof window !== "undefined" && window.ReactNativeWebView) {
    try {
      // jsPDF returns base64 (no data: prefix) when called with "datauristring"
      // and stripping the prefix; or use output('dataurlstring') and split.
      const dataUri = doc.output("datauristring");
      const base64 = dataUri.includes(",") ? dataUri.split(",")[1] : dataUri;
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: "SHARE_FILE",
          payload: { base64, filename, mimeType: "application/pdf" },
        })
      );
      return;
    } catch (err) {
      // Fall through to browser save if the bridge fails — at least try.
      // eslint-disable-next-line no-console
      console.warn("[PDF] native share failed, falling back to download:", err);
    }
  }

  // Web browser (or fallback): regular download.
  doc.save(filename);
};
